import React from 'react';
import { CheckCircle2, Clock, XCircle, AlertTriangle, ShieldCheck, Wrench, PackageCheck } from 'lucide-react';

export type StatusType = 
  | 'approved' | 'rejected' | 'pending' 
  | 'signed' | 'completed' 
  | 'assigned' | 'available' | 'in_stock' | 'maintenance' | 'in_service' | 'retired'
  | string;

interface StatusBadgeProps {
  status: StatusType;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode; defaultLabel: string }> = {
  // Onay & Form Durumları
  approved: {
    bg: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    defaultLabel: 'Onaylandı'
  },
  signed: {
    bg: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: <ShieldCheck className="w-3.5 h-3.5" />,
    defaultLabel: 'Dijital İmzalı'
  },
  rejected: {
    bg: 'bg-rose-50 text-rose-700',
    text: 'text-rose-700',
    border: 'border-rose-200',
    icon: <XCircle className="w-3.5 h-3.5" />,
    defaultLabel: 'Reddedildi'
  },
  pending: {
    bg: 'bg-amber-50 text-amber-700',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: <Clock className="w-3.5 h-3.5 animate-pulse" />,
    defaultLabel: 'Beklemede'
  },
  completed: {
    bg: 'bg-blue-50 text-blue-700',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    defaultLabel: 'Tamamlandı'
  },
  // Cihaz / Zimmet Durumları
  assigned: {
    bg: 'bg-indigo-50 text-indigo-700',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    icon: <PackageCheck className="w-3.5 h-3.5" />,
    defaultLabel: 'Zimmetli'
  },
  available: {
    bg: 'bg-green-50 text-green-700',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    defaultLabel: 'Stokta / Boşta'
  },
  in_stock: {
    bg: 'bg-green-50 text-green-700',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    defaultLabel: 'Stokta'
  },
  maintenance: {
    bg: 'bg-orange-50 text-orange-700',
    text: 'text-orange-700',
    border: 'border-orange-200',
    icon: <Wrench className="w-3.5 h-3.5" />,
    defaultLabel: 'Bakımda'
  },
  in_service: {
    bg: 'bg-orange-50 text-orange-700',
    text: 'text-orange-700',
    border: 'border-orange-200',
    icon: <Wrench className="w-3.5 h-3.5" />,
    defaultLabel: 'Serviste'
  },
  retired: {
    bg: 'bg-gray-100 text-gray-600',
    text: 'text-gray-600',
    border: 'border-gray-200',
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    defaultLabel: 'Hurda / İskonto'
  }
};

export default function StatusBadge({ status, label, size = 'md', className = '' }: StatusBadgeProps) {
  const normalizedKey = String(status || '').toLowerCase();
  const config = statusConfig[normalizedKey] || {
    bg: 'bg-gray-100 text-gray-700',
    text: 'text-gray-700',
    border: 'border-gray-200',
    icon: null,
    defaultLabel: String(status || 'Bilinmiyor')
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-xs font-medium gap-1.5',
    lg: 'px-3 py-1.5 text-sm font-medium gap-2'
  }[size];

  return (
    <span className={`inline-flex items-center rounded-full border ${config.bg} ${config.border} ${sizeClasses} ${className}`}>
      {config.icon}
      <span>{label || config.defaultLabel}</span>
    </span>
  );
}
